import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import AppIcon from './AppIcon'
import {
  DESCRIPTION_LINE_CLAMP,
  multiLineClamp,
  previewColors,
  singleLineClamp,
} from './previewTheme'
import { getConnectorFill } from './track'
import type { LiveActivityPlatformProps } from './types'

const t = buildTranslate('live_updates.preview')

const CARD_RADIUS = '18px'
const CARD_PADDING = '16px'
const STEP_DOT_SIZE = 11
// Connector between two dots — a 1px hairline centered on the dots' vertical
// axis (Figma 595:12980).
const CONNECTOR_HEIGHT = '1px'
// In-progress fill: the connector immediately after the active dot fills to
// half its width to read as "on the way to the next stage" (item 10, mock
// config-timer-mock). A full connector fill (both endpoints reached) is 100%.
const CONNECTOR_PARTIAL_WIDTH = '50%'

// Dot / connector colors on the dark glass card.
const FILLED_COLOR = palette.white
const EMPTY_COLOR = palette.grey30

// The iOS Live Activity card: a translucent dark "glass" card on the wallpaper
// with a dot-and-connector tracker (salvaged visuals from PR #3747), or — when
// confirmed-only (FR-03) — no tracker row at all, matching the real Live
// Activity (there's no "view detail" affordance on it).
const IosLiveActivityCard = ({
  content,
  appIconUrl,
  track,
  activeIndex,
  confirmedOnly,
}: LiveActivityPlatformProps) => {
  const [css] = useStyletron()

  // A hairline connector between two dots. `fill` drives how much of it reads
  // as "completed" (white) over the grey30 track. Wrapped in an 11px-tall box
  // so the 1px line sits on the dots' vertical center (Figma 595:12980).
  const renderBar = (
    key: string,
    fill: 'full' | 'partial' | 'none',
    testid?: string
  ) => (
    <div
      key={key}
      data-testid={testid}
      data-fill={testid ? fill : undefined}
      aria-hidden="true"
      className={css({
        flex: 1,
        height: `${STEP_DOT_SIZE}px`,
        display: 'flex',
        alignItems: 'center',
      })}
    >
      <div
        className={css({
          position: 'relative',
          width: '100%',
          height: CONNECTOR_HEIGHT,
          overflow: 'hidden',
          backgroundColor: EMPTY_COLOR,
        })}
      >
        {fill !== 'none' && (
          <div
            className={css({
              position: 'absolute',
              top: 0,
              left: 0,
              bottom: 0,
              width: fill === 'full' ? '100%' : CONNECTOR_PARTIAL_WIDTH,
              backgroundColor: FILLED_COLOR,
            })}
          />
        )}
      </div>
    </div>
  )

  const lastIndex = track.length - 1

  // { stage, index, isCompleted, color } is computed once and shared by both
  // the dot row and the label row below, so the two can't drift out of sync.
  const steps = track.map((stage, index) => {
    const isCompleted = index <= activeIndex
    return {
      stage,
      index,
      isCompleted,
      color: isCompleted ? FILLED_COLOR : EMPTY_COLOR,
    }
  })

  // Only the 4-step delivery track (DELIVERY_TRACK.length) is tight enough
  // for labels to need the smaller size — the 2/3-step pickup track has a
  // full extra gap's worth of room and keeps the card's normal BodyText5.
  const LabelText = track.length >= 4 ? Text.BodyText6 : Text.BodyText5

  const renderTracker = () => (
    <div>
      {/* Row of dots + hairline connectors. The first/last dots sit flush
          against the row's edges — matching the actual iOS Live Activity,
          which has no end inset — with equal-flex connectors between dots.
          Top-aligned so the bars line up with the dot centers (Figma
          595:12980). */}
      <div className={css({ display: 'flex', alignItems: 'center' })}>
        {steps.map(({ stage, index, isCompleted }) => (
          <React.Fragment key={stage}>
            {index > 0 &&
              renderBar(
                `connector-${index}`,
                getConnectorFill(activeIndex, index - 1),
                'lu-preview-connector'
              )}
            <div
              data-testid="lu-preview-step"
              data-completed={isCompleted}
              className={css({
                boxSizing: 'border-box',
                width: `${STEP_DOT_SIZE}px`,
                height: `${STEP_DOT_SIZE}px`,
                borderRadius: '50%',
                flexShrink: 0,
                // Active dots are solid; upcoming dots are hollow — a grey30
                // ring over the glass, not a filled grey circle.
                backgroundColor: isCompleted ? FILLED_COLOR : 'transparent',
                border: isCompleted ? 'none' : `1px solid ${EMPTY_COLOR}`,
              })}
            />
          </React.Fragment>
        ))}
      </div>
      {/* Labels render in their own row, anchored to each dot's actual
          position via CSS grid lines (not equal-width cells or flex gaps,
          both of which drift off the true dot position — see PR discussion).
          `repeat(lastIndex, 1fr)` lays lines down at 0%, 1/lastIndex, ...,
          100% — the same fractions the flush-edge dots + equal-flex
          connectors above resolve to. A grid item can't be pinned to a bare
          line (`grid-column: N / N` collapses to a 1-column span, not a
          zero-width point), so the first/last labels span only their own
          edge column (`justifySelf` start/end lands on that column's outer
          edge) and every middle label spans the two columns straddling its
          line so `justifySelf: center` lands exactly on their shared
          boundary. Those middle spans deliberately overlap in column terms,
          which would otherwise make Grid's auto-placement bump colliding
          labels onto separate rows — so every label is pinned to the same
          explicit `gridRow: 1`. */}
      <div
        className={css({
          display: 'grid',
          gridTemplateColumns: `repeat(${lastIndex}, 1fr)`,
          marginTop: '4px',
        })}
      >
        {steps.map(({ stage, index, color }) => {
          const label = t(`tracker.${stage}`)
          const isFirst = index === 0
          const isLast = index === lastIndex
          const gridColumn = isFirst
            ? '1 / 2'
            : isLast
            ? `${lastIndex} / ${lastIndex + 1}`
            : `${index} / ${index + 2}`
          const justifySelf = isFirst ? 'start' : isLast ? 'end' : 'center'
          return (
            <div
              key={stage}
              title={label}
              className={css({ gridRow: '1', gridColumn, justifySelf })}
            >
              <LabelText
                className={css({
                  display: 'block',
                  whiteSpace: 'nowrap',
                  color,
                })}
              >
                {label}
              </LabelText>
            </div>
          )
        })}
      </div>
    </div>
  )

  return (
    <div
      className={css({
        backgroundColor: previewColors.glassBackground,
        border: `1px solid ${previewColors.glassBorder}`,
        borderRadius: CARD_RADIUS,
        padding: CARD_PADDING,
      })}
    >
      {/* Header: app icon left, ETA pinned right (fixed OS chrome). */}
      <div
        className={css({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '8px',
        })}
      >
        <AppIcon appIconUrl={appIconUrl} />
        <Text.BodyText5 color="white">{t('eta')}</Text.BodyText5>
      </div>

      {/* Wrapper divs carry the native `title` tooltip so a merchant can
          still inspect the full text behind the ellipsis (Text doesn't
          forward arbitrary HTML attributes). */}
      <div title={content.title}>
        <Text.BodyText3
          bold
          color="white"
          className={css({ display: 'block', ...singleLineClamp })}
        >
          {content.title}
        </Text.BodyText3>
      </div>
      <div title={content.liveText}>
        <Text.BodyText4
          color="white"
          className={css({
            display: 'block',
            marginBottom: '16px',
            ...multiLineClamp(DESCRIPTION_LINE_CLAMP),
          })}
        >
          {content.liveText}
        </Text.BodyText4>
      </div>

      {!confirmedOnly && renderTracker()}
    </div>
  )
}

export default IosLiveActivityCard
